import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Voice capture (D-032): the browser's speech recognition dictates into the interview composer.
 * Nothing is sent anywhere by this module — the text goes through the same /turns call as typing,
 * so the interviewer, validation, and transcript are unchanged. Edge and Chrome support it;
 * Firefox and Safari on desktop do not, and the button simply does not appear there.
 */

interface RecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface RecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<RecognitionResultLike>;
}
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => RecognitionLike;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isDictationSupported(): boolean {
  return typeof window !== 'undefined' && recognitionCtor() !== null;
}

export interface Dictation {
  supported: boolean;
  listening: boolean;
  /** Words recognised so far in the current phrase, not yet final. */
  interim: string;
  error: string | null;
  start(): void;
  stop(): void;
}

const ERROR_TEXT: Record<string, string> = {
  'not-allowed': 'The browser blocked the microphone. Allow it for this site and try again.',
  'service-not-allowed': 'Speech recognition is not available in this browser session.',
  'audio-capture': 'No microphone was found.',
  network: 'Speech recognition needs a network connection.',
  'no-speech': '',
  aborted: '',
};

/** `onFinal` receives each finished phrase; append it to the draft. */
export function useDictation(onFinal: (text: string) => void, lang = 'en-US'): Dictation {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<RecognitionLike | null>(null);
  const wanted = useRef(false);
  const supported = isDictationSupported();

  const stop = useCallback(() => {
    wanted.current = false;
    rec.current?.stop();
    setListening(false);
    setInterim('');
  }, []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    setError(null);
    const r = new Ctor();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let live = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res[0].transcript;
        if (res.isFinal) onFinal(text.trim());
        else live += text;
      }
      setInterim(live.trim());
    };
    r.onerror = (e) => {
      const msg = ERROR_TEXT[e.error] ?? `Speech recognition stopped (${e.error}).`;
      if (msg) {
        setError(msg);
        wanted.current = false;
      }
    };
    r.onend = () => {
      // Browsers end continuous recognition after silence; restart while the owner still wants it.
      if (wanted.current) {
        try {
          r.start();
          return;
        } catch {
          /* fall through to stopped */
        }
      }
      setListening(false);
      setInterim('');
    };
    rec.current = r;
    wanted.current = true;
    r.start();
    setListening(true);
  }, [lang, onFinal]);

  useEffect(() => () => rec.current?.stop(), []);

  return { supported, listening, interim, error, start, stop };
}
